import { type ExtensionFieldValueObjectType } from "~/graphql/supergraphTypes";
import { parseDateOnlyString, formatDateOnlyString } from "../../../../utils/datetime";
import { ExtensionFieldCellEditor, CellEditorParams } from "./types";
import { CELL_EDITOR_STYLES, handleCellEditorKeyboard } from "./utils/cellEditorStyles";

/**
 * Date Cell Editor for date extension fields
 * Uses native HTML date input for consistent UX across browsers
 */
export class DateCellEditor implements ExtensionFieldCellEditor {
  private eGui: HTMLElement;
  private params!: CellEditorParams;
  private inputElement: HTMLInputElement;
  private currentValue: string;
  private initialValue: string;
  private isDestroyed: boolean = false;

  // Bind event handlers to maintain proper 'this' context
  private boundHandleInputChange: (event: Event) => void;
  private boundHandleKeyDown: (event: KeyboardEvent) => void;
  private boundHandleBlur: () => void;

  constructor() {
    this.eGui = document.createElement("div");
    Object.assign(this.eGui.style, CELL_EDITOR_STYLES.container);
    this.currentValue = "";
    this.initialValue = "";

    // Bind event handlers once to avoid memory leaks
    this.boundHandleInputChange = this.handleInputChange.bind(this);
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.boundHandleBlur = this.handleBlur.bind(this);

    this.inputElement = document.createElement("input");
    this.setupInputElement();
  }

  init(params: CellEditorParams): void {
    this.params = params;

    // Extract initial value from the extension field value object
    const extensionFieldValue = params.value as ExtensionFieldValueObjectType | null;
    const dateValue = extensionFieldValue?.dateValue;

    if (dateValue) {
      // Parse the date string using timezone-safe parser to avoid conversion issues
      const dateString = String(dateValue);
      const parsedDate = parseDateOnlyString(dateString);

      if (parsedDate) {
        // Use timezone-safe formatter to ensure consistent display
        this.initialValue = formatDateOnlyString(parsedDate);
      } else {
        // Fallback for non-standard date formats (shouldn't happen in normal usage)
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          this.initialValue = `${year}-${month}-${day}`;
        } else {
          this.initialValue = "";
        }
      }
    } else {
      this.initialValue = "";
    }

    this.currentValue = this.initialValue;
    this.inputElement.value = this.currentValue;
  }

  private setupInputElement(): void {
    this.inputElement.name = "cellValue";
    this.inputElement.type = "date";

    // Apply consistent styling using shared constants
    Object.assign(this.inputElement.style, {
      ...CELL_EDITOR_STYLES.input,
      textAlign: "left", // Override right alignment for date inputs
    });

    // Add event listeners using bound handlers
    this.inputElement.addEventListener("input", this.boundHandleInputChange);
    this.inputElement.addEventListener("keydown", this.boundHandleKeyDown);
    this.inputElement.addEventListener("blur", this.boundHandleBlur);

    this.eGui.appendChild(this.inputElement);
  }

  private handleInputChange(event: Event): void {
    if (this.isDestroyed) return;

    const target = event.target as HTMLInputElement;
    this.currentValue = target.value;
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.isDestroyed) return;

    // Use shared keyboard handler for Enter/Escape behavior
    handleCellEditorKeyboard(event, {
      inputElement: this.inputElement,
      initialValue: this.initialValue,
      currentValueSetter: (value) => {
        this.currentValue = value;
      },
      api: this.params.api,
    });
  }

  private handleBlur(): void {
    if (this.isDestroyed) return;
    this.params.api.stopEditing();
  }

  afterGuiAttached(): void {
    // Focus the input after the GUI is attached
    if (!this.isDestroyed && this.inputElement) {
      this.inputElement.focus();
    }
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  getValue(): string {
    return this.currentValue;
  }

  destroy(): void {
    this.isDestroyed = true;

    // Remove event listeners to prevent memory leaks
    if (this.inputElement) {
      this.inputElement.removeEventListener("input", this.boundHandleInputChange);
      this.inputElement.removeEventListener("keydown", this.boundHandleKeyDown);
      this.inputElement.removeEventListener("blur", this.boundHandleBlur);
    }
  }

  isPopup(): boolean {
    return false;
  }

  isCancelBeforeStart(): boolean {
    return false;
  }

  isCancelAfterEnd(): boolean {
    return false;
  }

  focusIn(): void {
    if (!this.isDestroyed && this.inputElement) {
      this.inputElement.focus();
    }
  }

  focusOut(): void {
    // No specific action needed on focus out
  }
}
